import {
  CUSTOM_MAP_OLD_LCV_ENGINE_QUERY,
  CUSTOM_MAP_OLD_LCV_MODEL_QUERY,
} from '@/const/service/maintenance-list/lcv';
import { getParsedData } from '@/contents/useMaintenanceList';
import type {
  ContentstackAllVehicleTypeMaintenanceOptionResponse,
  ContentstackLcvMaintenanceOptionResponse,
  MaintenanceOptionGroupedItem,
} from '@/types/services/contentstack/services/maintenance-service';

export const getLcvFirstOptionUniqueKey = (
  item: ContentstackLcvMaintenanceOptionResponse
) => {
  const title = (item?.title || '').toLowerCase();
  const isMhev = title.includes('mhev');
  const isMaxforce = title.includes('แมคซ์ฟอร์ซ');
  const marketing = isMhev
    ? item?.european_emission_standard
      ? `-${item?.european_emission_standard}-mhev`
      : '-mhev'
    : isMaxforce
      ? item?.european_emission_standard
        ? `-${item?.european_emission_standard}-maxforce`
        : '-maxforce'
      : item?.european_emission_standard
        ? `-${item?.european_emission_standard}`
        : '';
  return getParsedData(
    `${item?.vehicle_body_type}-${item?.model_year_begin}${marketing}`
  );
};

export const getLcvFirstOptionLabel = (
  item: ContentstackLcvMaintenanceOptionResponse | undefined
) => item?.title.slice(0, item?.title.indexOf(')') + 1);

export const getLcvSecondOptionLabel = (
  item: ContentstackLcvMaintenanceOptionResponse | undefined
) =>
  `${item?.engine_size} ${item?.transmission_system} ${item?.drive_system}${
    item?.class_one_marketing_grade
      ? ` (${item?.class_one_marketing_grade})`
      : ''
  }`;

export const getLcvSecondOptionUniqueKey = (
  item: ContentstackLcvMaintenanceOptionResponse
) => {
  const engineCore = `${item?.engine_size}-${item?.transmission_system}-${item?.drive_system}`;
  const classOneMarketingGrade = item?.class_one_marketing_grade
    ? `-${item?.class_one_marketing_grade}`
    : '';
  return getParsedData(`${engineCore}${classOneMarketingGrade}`);
};

const getTransmissionSystemFromParsedData = (data: string) => {
  switch (data) {
    case 'mt':
      return 'M/T';
    case 'at':
      return 'A/T';
  }
};
const getEuropeanEmissionStandardFromParsedData = (data: string) => {
  return data.includes('euro5') ? 'EURO 5' : undefined;
};

export const getContentStackItemFromLcvServiceMaintenancePageUrl = (
  url: string
) => {
  const splitStrings = url.split('_');
  // Correct URL should have more than 2 split strings
  if (splitStrings.length < 2) return undefined;

  const [
    parsedVehicleBodyType,
    parsedModelYearBegin,
    parsedEuropeanEmissionStandard,
    parsedMarketing, // mhev, maxforce
  ] = splitStrings[0].split('-');
  const [
    parsedEngineSize,
    parsedTranmissionSystem,
    parsedDriveSystem,
    parsedClassOneMarketingGrade,
  ] = splitStrings[1].split('-');

  let adjustedMarketing = parsedMarketing;
  if (adjustedMarketing === 'maxforce') {
    adjustedMarketing = 'แมคซ์ฟอร์ซ';
  }

  return {
    vehicle_body_type: parsedVehicleBodyType.toUpperCase(),
    model_year_begin: parseInt(parsedModelYearBegin),
    european_emission_standard: parsedEuropeanEmissionStandard
      ? getEuropeanEmissionStandardFromParsedData(
          parsedEuropeanEmissionStandard
        )
      : '',
    engine_size: parsedEngineSize,
    transmission_system: getTransmissionSystemFromParsedData(
      parsedTranmissionSystem
    ),
    drive_system: parsedDriveSystem ?? '',
    class_one_marketing_grade: parsedClassOneMarketingGrade ?? '',
    marketing: adjustedMarketing ?? '',
  };
};

export const getLcvServiceMaintenancePageUrlFormat = (
  item: ContentstackLcvMaintenanceOptionResponse
) => `${getLcvFirstOptionUniqueKey(item)}_${getLcvSecondOptionUniqueKey(item)}`;

export const getLcvSortedFirstOptions = (
  options: MaintenanceOptionGroupedItem[]
) => {
  const groupedByTypes: { [key: string]: MaintenanceOptionGroupedItem[] } = {};
  options.forEach((obj) => {
    const key = obj.children[0].vehicle_body_type;
    if (groupedByTypes[key]) {
      groupedByTypes[key] = [...groupedByTypes[key], obj];
    } else {
      groupedByTypes[key] = [obj];
    }
  });
  if (!groupedByTypes.PPV || !groupedByTypes.PICKUP) {
    const groupedByType = options.slice().sort((a, b) => {
      const groupItemA = a.children[0];
      const groupItemB = b.children[0];
      const typeA = groupItemA.vehicle_body_type;
      const typeB = groupItemB.vehicle_body_type;
      return typeA.localeCompare(typeB);
    });
    return groupedByType;
  }

  const orderedPPV = groupedByTypes.PPV.sort((a, b) => {
    const groupItemA = a.children[0];
    const groupItemB = b.children[0];
    return groupItemB.model_year_begin - groupItemA.model_year_begin;
  });
  const orderedPICKUP = groupedByTypes.PICKUP.sort((a, b) => {
    const groupItemA = a.children[0];
    const groupItemB = b.children[0];
    return groupItemB.model_year_begin - groupItemA.model_year_begin;
  });
  return [...orderedPICKUP, ...orderedPPV];
};

export const getLcvSortedSecondOptions = (
  options: ContentstackAllVehicleTypeMaintenanceOptionResponse[]
) => {
  return options
    .slice()
    .sort(
      (a, b) =>
        a.engine_size.localeCompare(b.engine_size) +
        a.drive_system.localeCompare(b.drive_system)
    );
};

export const getLcvSortedMux2025OptionByGrade = (
  options: ContentstackAllVehicleTypeMaintenanceOptionResponse[]
) => {
  const GRADE_SORT_ORDER = ['active', 'elegant', 'ultimate', 'rs'];
  const sortedOptionsByGrade = options.sort((a, b) => {
    const parsedAGrade = getParsedData(a.class_one_marketing_grade);
    const parsedBGrade = getParsedData(b.class_one_marketing_grade);
    return (
      a.engine_size.localeCompare(b.engine_size) +
        a.drive_system.localeCompare(b.drive_system) ||
      GRADE_SORT_ORDER.indexOf(parsedAGrade) -
        GRADE_SORT_ORDER.indexOf(parsedBGrade)
    );
  });
  return sortedOptionsByGrade;
};

//?model=isuzu-mu-x-e5&engine=19-at-4x2-mux_elegant&mileage=12-mon-20000-km
export const getLcvUpdateRedirectServiceMaintenancePageUrlFormat = (data: {
  model: keyof typeof CUSTOM_MAP_OLD_LCV_MODEL_QUERY;
  engine: keyof typeof CUSTOM_MAP_OLD_LCV_ENGINE_QUERY;
  mileage?: string;
}) => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  if (!CUSTOM_MAP_OLD_LCV_MODEL_QUERY[data.model]) return;

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const model = CUSTOM_MAP_OLD_LCV_MODEL_QUERY[data.model];
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const engine = CUSTOM_MAP_OLD_LCV_ENGINE_QUERY[data.engine] ?? data.engine;
  const schedule = data.mileage
    ? `?schedule=${data.mileage.split('-')[0]}-${data.mileage.split('-')[2]}`
    : '';

  return `${model}_${engine}${schedule}`;
};
